<template>
  <v-dialog
      v-model="showing" persistent
  >
    <div class="subscriber-editor">
      <v-card>
        <v-card-title class="subscriber-editor__header">
          {{ !!subscriber ? `Edit "${subscriber.description}"` : 'New Subscriber' }}
        </v-card-title>
        <v-card-text>
          <div class="subscriber-editor__data">
            <v-select
                ref="tenantSelectInput"
                v-model="subscriberData.tenant"
                :items="tenantOptions"
                return-object
                item-text="text"
                label="Select Tenant"
                persistent-placeholder
                placeholder="Select Tenant"
                dense
                filled
                outlined
                :rules="[rules.required]"
                :menu-props="{ bottom: true, offsetY: true }"
                @change="handleTenantSelection"
            />
            <v-select
                ref="channelSelectInput"
                v-model="subscriberData.channel"
                :items="channelTypeOptions"
                return-object
                item-text="label"
                label="Select Channel Type"
                persistent-placeholder
                placeholder="Select Channel Type"
                no-data-text="No channels available"
                hide-details
                dense
                filled
                outlined
                :rules="[rules.required]"
                :menu-props="{ bottom: true, offsetY: true }"
                @change="handleChannelTypeSelection"
            />
            <div
                v-if="!!subscriberData.channel && !!subscriberData.channel.specifications && !!receiverInfo"
                class="channel-specifications-cont"
            >
              <div class="channel-specifications__label">Specifications</div>
              <div class="channel-specifications">
                <div
                    v-for="(specificationItem, specificationIndex) in receiverInfo"
                    :key="`specification-key-${specificationIndex}`"
                    class="specification-item"
                >
                  <div class="specification-item__label">
                    {{ specificationItem.key }} :
                  </div>
                  <div class="specification-item__input">
                    <v-text-field
                        filled
                        solo
                        v-model="receiverInfo[specificationIndex].value"
                        :rules="[rules[specificationItem.key] || '']"
                        dense
                    />
                  </div>
                </div>
              </div>
            </div>
            <v-text-field
                ref="subscriberDescriptionInput"
                v-model="subscriberData.description"
                label="Description"
                placeholder="Description"
                persistent-placeholder
                :rules="[rules.required]"
                filled
                outlined
                dense
            />
            <v-text-field
                v-model.number="subscriberData.dailyQuota"
                label="Daily Quota"
                placeholder="Daily Quota"
                persistent-placeholder
                hide-spin-buttons
                type="number"
                filled
                outlined
                dense
            />
          </div>
        </v-card-text>
        <v-card-actions class="subscriber-editor__actions">
          <v-btn plain @click="closeEditor">
            Cancel
          </v-btn>
          <span @mouseenter="touchError">
          <v-btn
              color="primary"
              :loading="saving"
              :disabled="saving || !canSave"
              @click="saveSubscriberHandler"
          >
            Save
          </v-btn>
          </span>
        </v-card-actions>
      </v-card>
    </div>
  </v-dialog>
</template>

<script>
import {cloneDeep} from "lodash";
import {mapActions, mapGetters} from "vuex";

export default {
  name: 'SubscriberEditor',
  props: {
    saving: {
      type: Boolean,
      default: false,
    },
    show: {
      type: Boolean,
      default: false,
    },
    preselectedTenant: {
      type: Object,
      default: null
    },
    subscriber: {
      type: Object,
      default: null
    },
    channelTypes: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    subscriberData: {
      description: '',
      dailyQuota: null,
      tenant: null,
      channelTypeTenant: '',
      channel: '',
    },
    channelTypeOptions: [],
    receiverInfo: {},
    rules: {
      required: value => !!value || 'Required',
    },
  }),
  computed: {
    ...mapGetters("notifications", ["getTenants", "getChannelTypeTenants"]),
    showing: {
      get() {
        if ((this.show === true)) {
          if (!!this.subscriber && (!!this.subscriber.id) && (this.subscriber.id.length > 0)) {
            this.handleEditMode();
          }
          if (this.$refs.subscriberDescriptionInput !== undefined) {
            // this.$refs.subscriberDescriptionInput.reset();
          }
          if (!!this.preselectedTenant) {
            this.handlePreselectedTenant();
          }
        }
        return this.show;
      },
      set() {
        this.$emit('close')
      }
    },
    tenantOptions() {
      return this.getTenants.map(tenantItem => {
        return {text: tenantItem.name, value: tenantItem.id};
      });
    },
    canSave() {
      return (this.subscriberData.description.length > 0) && (!!this.subscriberData.tenant);
    }
  },
  methods: {
    ...mapActions("notifications", ["prepareData"]),
    /**
     * Trigger input fields validation for
     * - Tenant
     * - Channel type
     * - Description
     */
    touchError() {
      this.$refs.tenantSelectInput.validate(true);
      this.$refs.channelSelectInput.validate(true);
      // this.$refs.subscriberDescriptionInput.validate(true);
    },
    /**
     * handle the prop presence of a preselected Tenant
     */
    handlePreselectedTenant() {
      if (!!this.preselectedTenant) {
        this.subscriberData.tenant = {...this.preselectedTenant};
        this.handleTenantSelection(this.preselectedTenant);
      }
    },
    /**
     * handle the edit mode of the editor in the event of existing data prop
     */
    handleEditMode() {
      this.subscriberData.channel = this.subscriber.channel;
      this.subscriberData.dailyQuota = this.subscriber.dailyQuota;
      this.subscriberData.description = this.subscriber.description ?? '';
      this.subscriberData.channelTypeTenant = this.subscriber.channelTypeTenant;
      if (!!this.subscriber.channel && !!this.subscriber.channel.specifications && this.subscriber.receiverInfo) {
        const receiverInfoData = [];
        for (const key in this.subscriber.channel.specifications) {
          if (this.subscriber.receiverInfo.hasOwnProperty(key)) {
            const receiverInfoItem = {key: key, value: this.subscriber.receiverInfo[key]};
            receiverInfoData.push(receiverInfoItem);
          }
        }
        this.receiverInfo = receiverInfoData.reverse();
      }
      const selectedTenant = {text: this.subscriber.tenant.name, value: this.subscriber.tenant.id};
      this.subscriberData.tenant = selectedTenant;
      this.handleTenantSelection(selectedTenant);
    },
    /**
     * construct the validation rule needed for an input field as specified by the specificationItem
     * @param specificationItem {Object} - The subscriber/channel specification data to construct rules for
     */
    constructRule(specificationItem) {
      if (!!specificationItem) {
        // console.log('specificationItem', specificationItem);
        this.rules[specificationItem.key] = value => {
          const pattern = new RegExp(specificationItem.value);
          return pattern.test(value) || 'Invalid input.'
        }
      }
    },
    /**
     * Handle the selection of a Tenant and consecutively the available channel types for the corresponding tenant
     * @param tenantSelectionData {Object} - The selected tenant data
     */
    async handleTenantSelection(tenantSelectionData) {
      // console.log('SELECTED TENANT', tenantSelectionData);
      await this.prepareData();
      let availableChannels = [];
      this.getChannelTypeTenants.forEach(channelTypeTenantItem => {
        if ((channelTypeTenantItem.tenant_id === tenantSelectionData.value) && !!channelTypeTenantItem.channel) {
          // console.log('detected channel!', channelTypeTenantItem.channel);
          availableChannels.push(channelTypeTenantItem.channel);
        }
      });
      this.channelTypeOptions = availableChannels;
    },
    /**
     * Handle the selection of a channel type and check if it has any specifications to trigger the
     * constructRule method
     * @param channelSelectionData {Object} - The channel type data selected
     */
    handleChannelTypeSelection(channelSelectionData) {
      // console.log('SELECTED CHANNEL', this.subscriberData.channel);
      // console.log('SELECTED CHANNEL', channelSelectionData);
      if (!!channelSelectionData.specifications) {
        this.receiverInfo = [];
        const receiverInfoData = [];
        for (const key in channelSelectionData.specifications) {
          if (channelSelectionData.specifications.hasOwnProperty(key)) {
            // console.log(`Key: ${key}, Value: ${channelSelectionData.specifications[key]}`);
            const receiverInfoItem = {key: key, value: ''};
            // this.receiverInfo[key] = this.receiverInfo[key] || '';
            receiverInfoData.push(receiverInfoItem);
            this.constructRule({key: key, value: channelSelectionData.specifications[key]});
          }
        }
        this.receiverInfo = receiverInfoData.reverse();
      }
      let childQuota = 0;
      this.getChannelTypeTenants.forEach(channelTypeTenantItem => {
        if (channelSelectionData.id === channelTypeTenantItem.channelTypeId) {
          childQuota = channelTypeTenantItem.dailyQuotaForChild;
        }
      });
      this.subscriberData.dailyQuota = childQuota;
    },
    /**
     * Close the Subscriber Editor and reset the local data
     */
    closeEditor() {
      this.$emit('close');
      this.subscriberData = {
        description: '',
        dailyQuota: null,
        tenant: '',
        channelTypeTenant: '',
        channel: '',
      };
      this.channelTypeOptions = [];
      this.receiverInfo = {};
      this.rules = {
        required: value => !!value || 'Required',
      };
    },
    /**
     * Save the subscriber handler method.
     * construct the required subscriber data conditionally and
     * emit a save event along with the data parameters to be further handled by
     * the parent component
     */
    saveSubscriberHandler() {
      if ((this.saving === true) || (!this.canSave)) return;
      const channelData = {
        channelTypeTenant: '',
        description: '',
        ownerUsername: '',
        dailyQuota: 0,
        receiverInfo: {}
      };
      const refinedReceiverInfo = this.receiverInfo.reduce((accumulator, currentObj) => {
        accumulator[currentObj.key] = currentObj.value;
        return accumulator;
      }, {});
      // console.log('refinedReceiverInfo', refinedReceiverInfo);
      this.getChannelTypeTenants.forEach(channelTypeTenantItem => {
        if (
            (channelTypeTenantItem.tenant_id === this.subscriberData.tenant.value) &&
            (channelTypeTenantItem.channelTypeId === this.subscriberData.channel.id)
        ) {
          channelData.channelTypeTenant = channelTypeTenantItem.id;
          channelData.description = this.subscriberData.description ?? '';
          channelData.ownerUsername = '';
          channelData.dailyQuota = this.subscriberData.dailyQuota;
          channelData.receiverInfo = refinedReceiverInfo;
        }
      });
      if (!!this.subscriber && !!this.subscriber.id && (this.subscriber.id.length > 0)) {
        channelData.id = this.subscriber.id;
      }
      const channelDataParam = cloneDeep(channelData);
      // console.log('channelDataParam', channelDataParam);
      this.$emit('save', channelDataParam);
      this.closeEditor();
    }
  }
}
</script>

<style lang="scss" scoped>
.subscriber-editor {
  max-width: 512px;
  margin: 0 auto;
  border: 1px solid #1c6e59;

  &__header {
    margin-bottom: 12px;
  }

  &__data {
    display: flex;
    flex-direction: column;
    row-gap: 12px;

    .channel-specifications {
      display: flex;
      flex-direction: column-reverse;
      padding: 16px 8px 0;
      border-radius: 8px;

      &__label {
        color: #685AA1;
        font-size: 0.7em;
        font-weight: 600;
        text-transform: uppercase;
      }

      .specification-item {
        display: grid;
        grid-template-columns: 1fr 3fr;
        align-items: center;

        &__label {
          align-content: center;
          margin-top: -24px;
          padding: 0 16px;
        }
      }
    }
  }

  &__actions {
    display: flex;
    justify-content: end;
    align-content: center;
  }
}

.main-dark {
  .channel-specifications {
    background-color: rgba(250, 250, 250, 0.05);
  }
}

.main-light {
  .channel-specifications {
    background-color: rgba(0, 0, 0, 0.05);
  }
}
</style>